import { Alert, Box, Link } from "@mui/material";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Loader from "../components/general/Loader";
import NoData from "../components/general/NoData";

// Links are kept exactly as provided by the Power BI team (including the
// spaces inside $filter). Only the values between ' ' change per user.
const MANAGEMENT_REPORT_URL =
  "https://app.powerbi.com/reportEmbed?reportId=d4b672cf-2964-4157-9dfe-61fc9ecb0a84&autoAuth=true&ctid=c2e0d869-d16d-4e08-a48c-378d01518b1d";

const buildCountryPartnerReportUrl = (country) =>
  `https://app.powerbi.com/reportEmbed?reportId=9a8bd226-f641-4fab-87be-8183ad92b56b&autoAuth=true&ctid=c2e0d869-d16d-4e08-a48c-378d01518b1d&ReportSection53b217775dc3d5bd08f5&$filter=New_all_countries/display_name eq '${country}'`;

const buildSchoolReportUrl = (country, school) =>
  `https://app.powerbi.com/reportEmbed?reportId=81f1cb27-6805-4f78-9481-cc10cd7cc4f2&autoAuth=true&ctid=c2e0d869-d16d-4e08-a48c-378d01518b1d&$filter=New_all_countries/display_name eq '${country}' and New-schools/name eq '${school}'`;

// Spaces are left untouched. A ' is doubled (OData string escaping) and the
// few characters that would otherwise break the query string are encoded.
const toFilterValue = (value) =>
  String(value)
    .replace(/'/g, "''")
    .replace(/[%&#+]/g, (char) => encodeURIComponent(char));

const getCountryName = (country) =>
  typeof country === "string" ? country : country?.display_name;

const getSchoolName = (school) =>
  typeof school === "string" ? school : school?.name;

const getReportUrl = (user) => {
  const roleId = user.role_id;
  const country = getCountryName(user.country);
  const school = getSchoolName(user.school);

  // Admins (super admin / admin) are company employees -> management report
  if ([0, 1].includes(roleId)) {
    return MANAGEMENT_REPORT_URL;
  }

  // Country partner and partner assistant -> country report
  if ([2, 4].includes(roleId)) {
    return country ? buildCountryPartnerReportUrl(toFilterValue(country)) : null;
  }

  // Every other role (teacher, manager, ...) -> school report
  return country && school
    ? buildSchoolReportUrl(toFilterValue(country), toFilterValue(school))
    : null;
};

export default function Dashboard() {
  document.title = "Dashboard";
  const user = useSelector((state) => state.user);
  const reportUrl = useMemo(() => getReportUrl(user), [user]);
  const [loading, setLoading] = useState(true);

  return (
    <Box className="wrapperBox dashboard-wrapper">
      {reportUrl && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Please sign in with the account provided by SIMCC, if you don't have
          one contact your country partner administrator or{" "}
          <Link href="mailto:admin@simcc.org">admin@simcc.org</Link>
        </Alert>
      )}
      <Box className="dashboard-content">
        {!reportUrl ? (
          <NoData
            height="100%"
            text="No dashboard is available for your account."
          />
        ) : (
          <>
            {loading && (
              <Box className="dashboard-overlay">
                <Loader height="100%" />
              </Box>
            )}
            <Box className="powerbi-report-container">
              <iframe
                key={reportUrl}
                title="Dashboard"
                src={reportUrl}
                width="100%"
                height="100%"
                allowFullScreen
                onLoad={() => setLoading(false)}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
